import { useCallback, useEffect, useState } from 'react';
import { api, mensajeDe, type Cobro, type TorneoCompleto } from '../../services/api';
import { nombreDeTipo, textoDeTope } from '../../nucleo/habilitacion';
import { alTocar, ir } from '../../components/ruta';
import { Aviso, Boton, BotonVolver, Cargando, Tarjeta, Titulo } from '../../components/ui';
import { rangoDeFechas } from '../../nucleo/fechas';
import { ORIGEN_PUBLICO } from '../../nucleo/sitio';
import { NOMBRE_TEMA } from '../../nucleo/temas';
import type { EstadoTorneo } from '../../nucleo/validar';
import { capacidades } from '../../nucleo/opciones';
import { OpcionesDelTorneo } from './OpcionesTorneo';
import { FormTorneo } from './FormTorneo';
import { ETIQUETA_ESTADO } from './ListaTorneos';
import { FormCategoria, TarjetaCategoria } from './Categoria';
import { Programacion } from './Programacion';
import { Desplegable } from '../../components/Desplegable';
import { ControlCanchas } from './ControlCanchas';
import { InscripcionesTorneo, DisponibilidadTorneo, ImagenesTorneo, MensajesTorneo } from './Mejoras';

/** Hasta cuántas categorías se muestran todas abiertas. Con más, se abre de a una. */
const MAXIMO_ABIERTAS = 2;

const EXPLICACION_ESTADO: Record<EstadoTorneo, string> = {
  borrador: 'Solo lo ven los organizadores. Cargá todo tranquilo y publicalo cuando esté listo.',
  publicado: 'Cualquiera con el link lo ve. Es el estado del torneo mientras se juega.',
  terminado: 'Se sigue viendo, marcado como terminado.',
};

/**
 * Qué hace falta para publicar (etapa 7). Armar el torneo es gratis; publicarlo
 * necesita que esté habilitado: por la prueba gratis del club (si es chico), o
 * por el dueño del sitio al cobrar. El texto de cómo pagar lo escribe el dueño.
 */
function EstadoDeCobro({ cobro }: { cobro: Cobro }) {
  const { habilitacion: h, parejas } = cobro;
  const tiene = `Tiene ${parejas} pareja${parejas === 1 ? '' : 's'}.`;

  if (h) {
    const pasado = h.tope_parejas !== null && parejas > h.tope_parejas;
    return (
      <div className="mt-3">
        <Aviso tono={pasado ? 'alerta' : 'bien'}>
          Habilitado ({nombreDeTipo(h.tipo)}), {textoDeTope(h.tope_parejas)}. {tiene} La página del público se actualiza sola cada{' '}
          {h.tipo === 'prueba' ? cobro.minutos_prueba : cobro.minutos_pago} minutos
          {h.tipo === 'prueba' && cobro.minutos_prueba > cobro.minutos_pago ? ` (en un torneo habilitado, cada ${cobro.minutos_pago})` : ''}.
          {pasado && ` Para publicarlo así hay que ampliar la habilitación: ${cobro.texto_de_pago}`}
        </Aviso>
      </div>
    );
  }

  const entraEnLaPrueba = cobro.prueba_disponible && parejas <= cobro.prueba_tope_parejas;
  if (entraEnLaPrueba) {
    return (
      <div className="mt-3">
        <Aviso tono="bien">
          Este puede ser el torneo de prueba gratis de tu club: se publica sin pagar con hasta {cobro.prueba_tope_parejas} parejas. {tiene}
          {cobro.minutos_prueba > cobro.minutos_pago &&
            ` En la prueba, la página del público se actualiza cada ${cobro.minutos_prueba} minutos; en un torneo habilitado, cada ${cobro.minutos_pago}.`}
        </Aviso>
      </div>
    );
  }

  return (
    <div className="mt-3">
      <Aviso tono="alerta">
        Armar el torneo es gratis; para <b>publicarlo</b> hay que habilitarlo.{' '}
        {cobro.prueba_disponible && `La prueba gratis de tu club llega a ${cobro.prueba_tope_parejas} parejas y este tiene ${parejas}. `}
        <span className="mt-1 block whitespace-pre-line text-(--color-texto)">{cobro.texto_de_pago}</span>
      </Aviso>
    </div>
  );
}

export function EditarTorneo({ id, vista }: { id: number; vista?: 'control' | 'preparar' }) {
  const [datos, setDatos] = useState<TorneoCompleto | null>(null);
  const [error, setError] = useState('');
  const [editandoDatos, setEditandoDatos] = useState(false);
  const [nuevaCategoria, setNuevaCategoria] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const [abierta, setAbierta] = useState<number | null>(() => {
    try {
      return Number(localStorage.getItem(`torneos.categoriaAbierta.${id}`)) || null;
    } catch {
      return null;
    }
  });

  const recargar = useCallback(async () => {
    try {
      setDatos(await api.torneo(id));
      setError('');
    } catch (e) {
      setError(mensajeDe(e));
    }
  }, [id]);

  useEffect(() => {
    void recargar();
  }, [recargar]);

  // Con más de MAXIMO_ABIERTAS categorías, la pantalla es un acordeón: se abre de a
  // una, y cuál quedó abierta lo recuerda el navegador (el recargar que sigue a
  // cada acción no pierde el lugar).
  const alternar = (categoriaId: number) => {
    const nueva = abierta === categoriaId ? null : categoriaId;
    setAbierta(nueva);
    try {
      localStorage.setItem(`torneos.categoriaAbierta.${id}`, String(nueva ?? ''));
    } catch {
      // Sin localStorage (ventana privada): se pierde al recargar, nada más.
    }
  };

  if (!datos) return error ? <Aviso tono="error">{error}</Aviso> : <Cargando />;
  const acordeon = datos.categorias.length > MAXIMO_ABIERTAS;

  const { torneo, categorias } = datos;
  const efectivas = capacidades(datos.opciones);
  const vistaActual = efectivas.control ? vista ?? (torneo.estado === 'publicado' ? 'control' : 'preparar') : 'preparar';
  const link = `${ORIGEN_PUBLICO}/t/${torneo.slug}`;

  const cambiarEstado = async (estado: EstadoTorneo) => {
    try {
      await api.estadoTorneo(torneo.id, estado);
      await recargar();
    } catch (e) {
      // 402 = falta habilitarlo: el motivo y cómo pagar están en la tarjeta de Publicación.
      await recargar();
      setError(mensajeDe(e));
    }
  };

  const presentar = <T extends {dia: string | null; hora: string | null; cancha: number | null; en_juego_desde: number | null}>(p: T): T => ({...p,
    ...(!efectivas.horarios ? {dia: null, hora: null, cancha: efectivas.control && p.en_juego_desde !== null ? p.cancha : null} : {}),
    ...(!efectivas.control ? {en_juego_desde: null} : {})});
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      setError('No se pudo copiar: copialo a mano.');
    }
  };

  return (
    <>
      <BotonVolver texto="Torneos" onClick={() => ir('/admin')} />
      <header>
        <h1 className="font-titulo text-4xl uppercase">{torneo.nombre}</h1>
        <p className="text-sm text-(--color-apagado)">
          {rangoDeFechas(torneo.fecha_desde, torneo.fecha_hasta)}
          {torneo.sede ? ` · ${torneo.sede}` : ''} · colores {NOMBRE_TEMA[torneo.tema]}
          {torneo.canchas ? ` · ${torneo.canchas} canchas` : ''}
        </p>
      </header>

      {error && <Aviso tono="error">{error}</Aviso>}
      {vista === 'control' && !efectivas.control && <Aviso>Control de canchas está desactivado. Estás en Preparar torneo; podés activarlo en Opciones.</Aviso>}

      <nav aria-label="Manejo del torneo" className="grid grid-cols-2 gap-2">
        {(['preparar', ...(efectivas.control ? ['control' as const] : [])] as const).map(v => <a key={v} href={`/admin/t/${id}/${v}`} onClick={alTocar(`/admin/t/${id}/${v}`)} aria-current={vistaActual === v ? 'page' : undefined} className={`flex min-h-12 items-center justify-center rounded-xl border px-2 text-center text-sm font-semibold ${vistaActual === v ? 'border-(--color-acento) bg-(--color-acento) text-(--color-sobre-acento)' : 'border-(--color-borde) bg-(--color-panel) text-(--color-apagado)'}`}>{v === 'control' ? 'Control de canchas' : 'Preparar torneo'}</a>)}
      </nav>

      {vistaActual === 'control' ? <ControlCanchas datos={datos} recargar={recargar} /> : <>

      <Tarjeta>
        <Titulo>Publicación</Titulo>
        <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Estado del torneo">
          {(Object.keys(ETIQUETA_ESTADO) as EstadoTorneo[]).map((e) => (
            <button
              key={e}
              role="radio"
              aria-checked={torneo.estado === e}
              onClick={() => cambiarEstado(e)}
              className={`min-h-11 rounded-xl border text-sm font-semibold ${
                torneo.estado === e ? ETIQUETA_ESTADO[e].clase + ' bg-(--color-panel)' : 'border-(--color-borde) text-(--color-apagado)'
              }`}
            >
              {ETIQUETA_ESTADO[e].texto}
            </button>
          ))}
        </div>
        <p className="mt-2 text-sm text-(--color-apagado)">{EXPLICACION_ESTADO[torneo.estado]}</p>
        <EstadoDeCobro cobro={datos.cobro} />

        <div className="mt-4 flex flex-col gap-2">
          <span className="text-sm font-semibold text-(--color-apagado)">Link para compartir</span>
          <code className="break-all rounded-xl border border-(--color-borde) bg-(--color-panel) px-3 py-2 text-sm">{link}</code>
          <div className="flex gap-2">
            <Boton chico onClick={copiar}>
              {copiado ? '¡Copiado!' : 'Copiar link'}
            </Boton>
            <a
              href={`/t/${torneo.slug}`}
              target="_blank"
              rel="noopener"
              className="flex min-h-9 items-center rounded-xl border border-(--color-borde) bg-(--color-panel) px-3 text-sm"
            >
              Ver como el público ↗
            </a>
          </div>
        </div>
      </Tarjeta>

      <Tarjeta>
        <Titulo>Datos</Titulo>
        {editandoDatos ? (
          <FormTorneo
            inicial={{ nombre: torneo.nombre, sede: torneo.sede, fecha_desde: torneo.fecha_desde, fecha_hasta: torneo.fecha_hasta, tema: torneo.tema, canchas: torneo.canchas }}
            textoBoton="Guardar"
            alGuardar={async (d) => {
              await api.editarTorneo(torneo.id, d);
              setEditandoDatos(false);
              await recargar();
            }}
            alCancelar={() => setEditandoDatos(false)}
          />
        ) : (
          <>
            <p className="text-sm text-(--color-apagado)">
              El link no cambia aunque cambies el nombre: es el que ya circuló.
            </p>
            <div className="mt-3">
              <Boton chico tipo="secundario" onClick={() => setEditandoDatos(true)}>
                Editar nombre, fechas, sede, canchas o colores
              </Boton>
            </div>
          </>
        )}
      </Tarjeta>

      <OpcionesDelTorneo key={JSON.stringify(datos.opciones)} id={id} inicial={datos.opciones} recargar={recargar} />
      <InscripcionesTorneo datos={datos} recargar={recargar} />
      {efectivas.disponibilidad && <DisponibilidadTorneo key={JSON.stringify(datos.disponibilidad)} datos={datos} recargar={recargar} />}
      {efectivas.imagenes && <ImagenesTorneo datos={datos} recargar={recargar} />}

      <h2 className="mt-2 flex items-baseline gap-2 font-titulo text-3xl uppercase">
        Categorías
        {categorias.length > 0 && (
          <span className="font-sans text-sm normal-case tracking-normal text-(--color-apagado)">
            {datos.cobro.parejas} parejas en {categorias.length} categoría{categorias.length === 1 ? '' : 's'}
          </span>
        )}
      </h2>
      {categorias.length === 0 && !nuevaCategoria && (
        <Aviso>Todavía no hay categorías. Creá la primera (ej: 5ta Caballeros) y después pegá la lista de parejas.</Aviso>
      )}

      {categorias.map((c) => (
        <TarjetaCategoria
          key={c.id}
          categoria={{...c, premios: efectivas.premios ? c.premios : [], grupos: c.grupos.map(g => ({...g, partidos: g.partidos.map(presentar)})), cuadro: c.cuadro ? {...c.cuadro, partidos: c.cuadro.partidos.map(presentar)} : null}}
          premiosActivos={efectivas.premios}
          horariosActivos={efectivas.horarios}
          temaTorneo={torneo.tema}
          recargar={recargar}
          abierta={!acordeon || abierta === c.id}
          alAlternar={acordeon ? () => alternar(c.id) : undefined}
        />
      ))}

      {nuevaCategoria || categorias.length === 0 ? (
        <Tarjeta>
          <Titulo>Nueva categoría</Titulo>
          <FormCategoria
            textoBoton="Crear categoría"
            alGuardar={async (d) => {
              await api.crearCategoria(torneo.id, d);
              setNuevaCategoria(false);
              await recargar();
            }}
            alCancelar={categorias.length > 0 ? () => setNuevaCategoria(false) : undefined}
          />
        </Tarjeta>
      ) : (
        <Boton tipo="secundario" onClick={() => setNuevaCategoria(true)}>
          + Nueva categoría
        </Boton>
      )}

      {efectivas.horarios && <Desplegable titulo="Programación" resumen="Horarios, canchas y organización de los partidos">
        <Programacion datos={datos} recargar={recargar} />
      </Desplegable>}
      {efectivas.mensajes && <MensajesTorneo key={`${datos.opciones.modo_horarios}-${datos.opciones.inscripcion_publica}-${torneo.estado}`} datos={datos} />}
      </>}
    </>
  );
}
